import React from 'react';
import {
  Box, Paper, Typography, Grid, Button, Chip, Divider, Select, MenuItem, TextField,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Collapse, Tooltip, Link,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import Store from '../../utilities/store';
import constants from '../../utilities/constants';
import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import usePermissions from '../../utilities/usePermissions';
import { useSelectMenuProps } from '../../utilities/ModalStackContext';

// Load lifecycle, not the car's derived shipping status — a car can sit on a
// cancelled load, which ShippingStatusChip deliberately ignores.
const LOAD_STATUS_META = {
  pending:     { label: 'Pending',     color: 'default' },
  posted:      { label: 'Posted',      color: 'info' },
  in_progress: { label: 'In progress', color: 'warning' },
  completed:   { label: 'Completed',   color: 'success' },
  cancelled:   { label: 'Cancelled',   color: 'error' },
};

const DEFAULT_VEHICLE_TYPE = 'coupe_2_doors';
const LOW_CONFIDENCE = 50;

// Every shipping figure on this tab is shown with a flat margin folded in, so a
// rep can read the number off the screen and quote it as-is. It is applied at
// render only — the stored estimate keeps the raw market number — and it is
// never broken out as its own line, label or tooltip.
const QUOTE_MARGIN = 250;

const marked = (n) => ((n === null || n === undefined || isNaN(n)) ? n : Number(n) + QUOTE_MARGIN);

const money = (n) => (n === null || n === undefined || isNaN(n))
  ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const loadDate = (load) => load.departure_date || load.completed_at || load.created_at || null;

const fmtDate = (d) => (d ? moment(new Date(d)).format('MMM D, YYYY') : '—');

const zipOf = (value) => {
  const zip = String(value || '').trim().slice(0, 5);
  return /^\d{5}$/.test(zip) ? zip : '';
};

// Either end of the lane can be a Skyway lot, depending on the direction.
const lotFor = (code) => constants.skyway_locations[code] || constants.skyway_locations.FL;

// An estimate older than this is still shown, but flagged. The upstream number
// is a 90-day rolling median, so a month-old quote has drifted.
const STALE_AFTER_DAYS = 30;

const placeLabel = (loc) => (loc && loc.zip) ? loc.zip : [loc && loc.city, loc && loc.state].filter(Boolean).join(', ');

// A Skyway end is stored as a bare ZIP, so name the lot it belongs to — '34211'
// on its own tells nobody which end of a saved lane they are looking at.
const lotNameForZip = (zip) => {
  const match = Object.entries(constants.skyway_locations).find(([, info]) => info.zip === zip);
  return match ? `${match[1].label} (${zip})` : '';
};

const isInbound = (entry) => entry.direction === 'in';

// Which end of a saved entry is the customer. Only that end kept a street
// address (`dest_line`); the Skyway end is the lot. Entries written before
// inbound quoting have no `direction` and are all Skyway → customer.
const entryEnd = (entry, side) => {
  const partySide = isInbound(entry) ? 'pickup' : 'delivery';
  const place = entry[side] || null;
  if (side === partySide) return entry.dest_line || placeLabel(place) || '—';
  return lotNameForZip(place && place.zip) || placeLabel(place) || '—';
};

// Identifies one quote — same lane, same vehicle, same condition. Re-running it
// replaces the stored entry rather than adding another.
const estimateKey = ({ pickup, delivery, vehicle }) => [
  placeLabel(pickup),
  placeLabel(delivery),
  vehicle.type,
  vehicle.year,
  (vehicle.make || '').toLowerCase(),
  (vehicle.model || '').toLowerCase(),
  vehicle.is_inoperable ? 'inop' : 'op',
].join('|');

// Headline numbers only. `recent_moves` is far too big to keep 10 of on the car
// doc, so a recalled estimate has no comps — re-running fetches them again, and
// almost always off the server's own 7-day cache.
const slimResult = (r) => {
  if (!r) return null;
  if (!r.ok) return { ok: false, error: r.error || 'No price available', code: r.code || null };
  return {
    ok: true,
    price: r.price ?? null,
    price_range_lower: r.price_range_lower ?? null,
    price_range_upper: r.price_range_upper ?? null,
    price_per_mile: r.price_per_mile ?? null,
    confidence: r.confidence ?? null,
    volume: r.volume ?? null,
    distance_miles: r.distance_miles ?? null,
    min_expected_time_to_dispatch: r.min_expected_time_to_dispatch ?? null,
    max_expected_time_to_dispatch: r.max_expected_time_to_dispatch ?? null,
  };
};

// A stored entry rendered back into the shape the result view expects.
const entryToEstimate = (entry) => ({
  pickup: entry.pickup,
  delivery: entry.delivery,
  vehicle: entry.vehicle,
  open: entry.open,
  enclosed: entry.enclosed,
  recalled: entry,
});

export default function Transport({ car = {} }) {
  const stockNumber = car.stock;
  const { isAllowed, isAdmin } = usePermissions();
  const canSeeCharge = isAllowed('car-financials') || isAdmin();
  const selectMenuProps = useSelectMenuProps();

  // ── Load history ──────────────────────────────────────────────────────────
  const [historyLoading, setHistoryLoading] = React.useState(true);
  const [loadHistory, setLoadHistory] = React.useState([]);

  React.useEffect(() => {
    let active = true;
    if (!stockNumber) { setLoadHistory([]); setHistoryLoading(false); return () => {}; }
    setHistoryLoading(true);
    Store.getLoadHistoryForCar(stockNumber)
      .then(entries => { if (active) { setLoadHistory(entries); setHistoryLoading(false); } })
      .catch(() => {
        if (!active) return;
        setLoadHistory([]);
        setHistoryLoading(false);
        StateManager.setAlertAndOpen('Could not read this car’s load history', 'error');
      });
    return () => { active = false; };
  }, [stockNumber]);

  // ── Estimate inputs ───────────────────────────────────────────────────────
  const lot = lotFor(car.location);
  const buyer = car.buyer || {};
  const alt = car.alt_shipping_address || null;
  const buyerHasAddress = !!(buyer.zip || (buyer.city && buyer.state));
  const altHasAddress = !!(alt && alt.addressLine1 && (alt.postalCode || (alt.cityLocality && alt.stateProvince)));

  // 'out' is Skyway → customer, 'in' is customer → Skyway. Only which end is
  // pickup and which is delivery changes; both sides keep their own inputs.
  const [direction, setDirection] = React.useState('out');
  const [lotZip, setLotZip] = React.useState(lot.zip);
  const [lotCode, setLotCode] = React.useState(constants.skyway_locations[car.location] ? car.location : 'FL');
  const [partyMode, setPartyMode] = React.useState(altHasAddress ? 'alternate' : 'buyer');
  const [customZip, setCustomZip] = React.useState('');
  const [customCity, setCustomCity] = React.useState('');
  const [customState, setCustomState] = React.useState('');
  const [vehicleType, setVehicleType] = React.useState(
    constants.shipping_vehicle_type_by_body_style[car.body] || DEFAULT_VEHICLE_TYPE,
  );
  const [inoperable, setInoperable] = React.useState(false);

  const [estimating, setEstimating] = React.useState(false);
  const [estimate, setEstimate] = React.useState(null);
  const [estimateError, setEstimateError] = React.useState(null);
  const [showPrevious, setShowPrevious] = React.useState(false);

  // Remembered estimates ride along on the car doc, and CarModal's live
  // onSnapshot keeps them current, so recall costs nothing and needs no fetch.
  const savedEstimates = React.useMemo(
    () => (Array.isArray(car.shipping_estimates) ? car.shipping_estimates.filter(Boolean) : []),
    [car.shipping_estimates],
  );

  // Display a saved estimate AND rewind the form to the inputs that produced it.
  // Without the second half, Refresh would silently re-price whatever the form
  // happens to be showing instead of the quote on screen.
  const applyEntry = React.useCallback((entry) => {
    setEstimate(entryToEstimate(entry));
    setEstimateError(null);
    // Entries saved before inbound quoting existed are all Skyway → customer.
    const dir = entry.direction === 'in' ? 'in' : 'out';
    setDirection(dir);
    const lotSide = dir === 'in' ? entry.delivery : entry.pickup;
    const partySide = dir === 'in' ? entry.pickup : entry.delivery;
    if (lotSide && lotSide.zip) {
      setLotZip(lotSide.zip);
      // Keep the lot dropdown honest about which zip is in the box.
      const match = Object.entries(constants.skyway_locations).find(([, info]) => info.zip === lotSide.zip);
      if (match) setLotCode(match[0]);
    }
    if (entry.dest_mode) setPartyMode(entry.dest_mode);
    if (entry.dest_mode === 'custom' && partySide) {
      setCustomZip(partySide.zip || '');
      setCustomCity(partySide.city || '');
      setCustomState(partySide.state || '');
    }
    if (entry.vehicle) {
      if (entry.vehicle.type) setVehicleType(entry.vehicle.type);
      setInoperable(!!entry.vehicle.is_inoperable);
    }
  }, []);

  // Show the last quote the moment the tab opens. That is the whole point of
  // keeping them — nobody should press the button to see a price they already
  // paid for. A fresh fetch replaces it and takes precedence from then on.
  const recalledOnce = React.useRef(false);
  React.useEffect(() => {
    if (recalledOnce.current || estimate || !savedEstimates.length) return;
    recalledOnce.current = true;
    applyEntry(savedEstimates[0]);
  }, [savedEstimates, estimate, applyEntry]);

  // An unsold car has no buyer, so don't sit on a mode that can't resolve.
  React.useEffect(() => {
    if (partyMode === 'buyer' && !buyerHasAddress) setPartyMode(altHasAddress ? 'alternate' : 'custom');
  }, [partyMode, buyerHasAddress, altHasAddress]);

  const onLotChange = (code) => {
    setLotCode(code);
    setLotZip(lotFor(code).zip);
  };

  const inbound = direction === 'in';

  // The customer / custom end of the lane, whichever end that is.
  const party = React.useMemo(() => {
    if (partyMode === 'buyer') {
      return { zip: zipOf(buyer.zip), city: buyer.city || '', state: buyer.state || '' };
    }
    if (partyMode === 'alternate' && alt) {
      return { zip: zipOf(alt.postalCode), city: alt.cityLocality || '', state: alt.stateProvince || '' };
    }
    return { zip: zipOf(customZip), city: customCity, state: customState };
  }, [partyMode, buyer.zip, buyer.city, buyer.state, alt, customZip, customCity, customState]);

  const partyLine = React.useMemo(() => {
    if (partyMode === 'buyer') {
      return [buyer.address1, buyer.city, buyer.state, buyer.zip].filter(Boolean).join(', ') || 'No buyer address on file';
    }
    if (partyMode === 'alternate' && alt) {
      return [alt.addressLine1, alt.cityLocality, alt.stateProvince, alt.postalCode].filter(Boolean).join(', ');
    }
    return [customCity, customState, customZip].filter(Boolean).join(', ');
    // Primitive fields, not the `buyer`/`alt` objects — `car.buyer || {}` makes a
    // fresh object every render, which would defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyMode, buyer.address1, buyer.city, buyer.state, buyer.zip, alt, customCity, customState, customZip]);

  const lotPlace = React.useMemo(() => ({ zip: lotZip }), [lotZip]);
  const pickup = inbound ? party : lotPlace;
  const delivery = inbound ? lotPlace : party;

  // What the form would price right now. Compared against a displayed saved
  // estimate so a price for one lane never sits under inputs for another.
  const formKey = React.useMemo(() => estimateKey({
    pickup,
    delivery,
    vehicle: { type: vehicleType, year: String(car.year || ''), make: car.make || '', model: car.model || '', is_inoperable: inoperable },
  }), [pickup, delivery, vehicleType, inoperable, car.year, car.make, car.model]);

  const staleInputs = !!(estimate && estimate.recalled && estimate.recalled.key !== formKey);

  const partyReady = !!(party.zip || (party.city && party.state.length === 2));
  const vehicleReady = !!(car.year && car.make && car.model);
  const canEstimate = !!lotZip && partyReady && vehicleReady && !estimating;

  const runEstimate = async (force = false) => {
    setEstimating(true);
    setEstimateError(null);
    const response = await RequestManager.post({
      function: 'getShippingEstimate',
      variables: {
        force,
        pickup,
        delivery,
        vehicle: {
          type: vehicleType,
          year: String(car.year || ''),
          make: car.make || '',
          model: car.model || '',
          is_inoperable: inoperable,
        },
      },
    });
    setEstimating(false);

    if (!response || response.success === false) {
      const message = (response && response.error) || 'Could not get a shipping estimate';
      setEstimateError(message);
      setEstimate(null);
      StateManager.setAlertAndOpen(message, 'error');
      return;
    }
    setEstimate(response.data);

    // Remember it. Optimistic background write — the price is already on screen,
    // so this only ever needs to speak up if it fails.
    const { pickup: ranPickup, delivery: ranDelivery, vehicle } = response.data;
    Store.recordShippingEstimate(stockNumber, {
      ran_at: new Date().toISOString(),
      ran_by: StateManager.userID || '',
      key: estimateKey({ pickup: ranPickup, delivery: ranDelivery, vehicle }),
      pickup: ranPickup,
      delivery: ranDelivery,
      direction,
      // Named for the customer end of the lane, which is the destination on an
      // outbound quote and the pickup on an inbound one.
      dest_mode: partyMode,
      dest_line: partyLine,
      vehicle,
      open: slimResult(response.data.open),
      enclosed: slimResult(response.data.enclosed),
    }).catch(err => {
      console.warn('Could not save the shipping estimate:', err);
      StateManager.setAlertAndOpen('Estimate ran, but saving it for next time failed', 'warning');
    });
  };

  return (
    <Box sx={{ pb: 2 }}>
      <LoadHistoryCard
        loading={historyLoading}
        entries={loadHistory}
        canSeeCharge={canSeeCharge}
      />

      <Paper sx={{ p: 2, mt: '3px' }} elevation={0}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Shipping estimate</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          Market rate from Super Dispatch, median of the last 90 days of dispatched loads on this lane.
        </Typography>

        <ToggleButtonGroup
          exclusive size="small" value={direction}
          onChange={(e, next) => { if (next) setDirection(next); }}
          data-sold-ok
          sx={{ mb: 2 }}
        >
          <ToggleButton value="out" sx={{ px: 2 }}>
            <LocalShippingIcon fontSize="small" sx={{ mr: 0.75 }} />
            Skyway &rarr; customer
          </ToggleButton>
          <ToggleButton value="in" sx={{ px: 2 }}>
            <CallReceivedIcon fontSize="small" sx={{ mr: 0.75 }} />
            Customer &rarr; Skyway
          </ToggleButton>
        </ToggleButtonGroup>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={3} sx={{ order: inbound ? 2 : 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {inbound ? 'Deliver to' : 'Pick up from'}
            </Typography>
            <Select
              fullWidth size="small" value={lotCode} MenuProps={selectMenuProps}
              onChange={(e) => onLotChange(e.target.value)}
              sx={{ mt: 0.5 }}
            >
              {Object.entries(constants.skyway_locations).map(([code, info]) => (
                <MenuItem key={code} value={code}>{info.label}</MenuItem>
              ))}
            </Select>
            <TextField
              fullWidth size="small" label={inbound ? 'Destination ZIP' : 'Origin ZIP'} value={lotZip}
              onChange={(e) => setLotZip(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
              sx={{ mt: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={5} sx={{ order: inbound ? 1 : 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {inbound ? 'Pick up from' : 'Deliver to'}
            </Typography>
            <ToggleButtonGroup
              exclusive size="small" value={partyMode}
              onChange={(e, next) => { if (next) setPartyMode(next); }}
              data-sold-ok
              sx={{ mt: 0.5, display: 'flex' }}
            >
              <ToggleButton value="buyer" disabled={!buyerHasAddress} sx={{ flex: 1 }}>Buyer</ToggleButton>
              {altHasAddress && <ToggleButton value="alternate" sx={{ flex: 1 }}>Alternate</ToggleButton>}
              <ToggleButton value="custom" sx={{ flex: 1 }}>Custom</ToggleButton>
            </ToggleButtonGroup>

            {partyMode === 'custom' ? (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={5}>
                  <TextField
                    fullWidth size="small" label="ZIP" value={customZip}
                    onChange={(e) => setCustomZip(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="City" value={customCity}
                    onChange={(e) => setCustomCity(e.target.value)} />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth size="small" label="State" value={customState}
                    onChange={(e) => setCustomState(e.target.value.toUpperCase().slice(0, 2))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    A ZIP prices the lane more precisely than a city.
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                {partyLine || (inbound ? 'Enter a pickup location' : 'Enter a destination')}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4} sx={{ order: 3 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Vehicle</Typography>
            <Select
              fullWidth size="small" value={vehicleType} MenuProps={selectMenuProps}
              onChange={(e) => setVehicleType(e.target.value)}
              sx={{ mt: 0.5 }}
            >
              {constants.shipping_vehicle_types.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
              {car.body
                ? `Body style on file: ${car.body}. Super Dispatch prices only six types — change it if this is wrong.`
                : 'No body style on file — confirm the type, it changes the price.'}
            </Typography>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={<Checkbox size="small" checked={inoperable} onChange={(e) => setInoperable(e.target.checked)} />}
              label={<Typography variant="body2">Inoperable / won&rsquo;t roll</Typography>}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
          <Button variant="contained" onClick={() => runEstimate(false)} disabled={!canEstimate}>
            {estimating ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Get estimate'}
          </Button>
          {estimate && (
            <Tooltip title="Ignore the cached result and ask Super Dispatch again">
              <Button size="small" startIcon={<RefreshIcon />} onClick={() => runEstimate(true)} disabled={estimating}>
                Refresh
              </Button>
            </Tooltip>
          )}
          {!vehicleReady && (
            <Typography variant="caption" sx={{ color: 'warning.dark' }}>
              Needs the car&rsquo;s year, make and model.
            </Typography>
          )}
          {vehicleReady && !partyReady && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Add {inbound ? 'a pickup' : 'a destination'} ZIP, or a city and state.
            </Typography>
          )}
        </Box>

        {estimateError && (
          <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>{estimateError}</Typography>
        )}

        {estimate && (
          <>
            {estimate.recalled && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <Chip size="small" variant="outlined" label="Saved estimate" />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Ran {moment(estimate.recalled.ran_at).fromNow()}
                  {estimate.recalled.ran_by ? ` by ${constants.userNames[estimate.recalled.ran_by] || estimate.recalled.ran_by}` : ''}
                  {` · ${entryEnd(estimate.recalled, 'pickup')} → ${entryEnd(estimate.recalled, 'delivery')}`}
                </Typography>
              </Box>
            )}
            {estimate.recalled && moment().diff(moment(estimate.recalled.ran_at), 'days') >= STALE_AFTER_DAYS && (
              <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', mt: 0.5 }}>
                Over {STALE_AFTER_DAYS} days old — the market has moved since. Refresh before quoting it.
              </Typography>
            )}
            {staleInputs && (
              <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', mt: 0.5 }}>
                This price is for the saved route above, not the inputs you just changed. Press Get estimate.
              </Typography>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}><TrailerResult title="Open" result={estimate.open} /></Grid>
              <Grid item xs={12} md={6}><TrailerResult title="Enclosed" result={estimate.enclosed} /></Grid>
            </Grid>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
              {lotNameForZip(estimate.pickup && estimate.pickup.zip) || placeLabel(estimate.pickup)}
              {' → '}
              {lotNameForZip(estimate.delivery && estimate.delivery.zip) || placeLabel(estimate.delivery)}
              {' · '}
              {estimate.vehicle.year} {estimate.vehicle.make} {estimate.vehicle.model}
            </Typography>
          </>
        )}

        {savedEstimates.length > 1 && (
          <>
            <Button
              size="small"
              sx={{ mt: 2 }}
              endIcon={showPrevious ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowPrevious(v => !v)}
              data-sold-ok
            >
              {savedEstimates.length} saved estimate{savedEstimates.length === 1 ? '' : 's'}
            </Button>
            <Collapse in={showPrevious}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ran</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">Enclosed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {savedEstimates.map((entry) => {
                    const active = estimate && estimate.recalled && estimate.recalled.key === entry.key;
                    return (
                      <TableRow
                        key={entry.key}
                        hover
                        selected={!!active}
                        onClick={() => applyEntry(entry)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="caption">{fmtDate(entry.ran_at)}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="caption">{entryEnd(entry, 'pickup')}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="caption">{entryEnd(entry, 'delivery')}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {(constants.shipping_vehicle_types.find(t => t.value === entry.vehicle.type) || {}).label || entry.vehicle.type}
                            {entry.vehicle.is_inoperable ? ' · inop' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">{entry.open && entry.open.ok ? money(marked(entry.open.price)) : '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">{entry.enclosed && entry.enclosed.ok ? money(marked(entry.enclosed.price)) : '—'}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Collapse>
          </>
        )}
      </Paper>
    </Box>
  );
}

// ── Load history ────────────────────────────────────────────────────────────
function LoadHistoryCard({ loading, entries, canSeeCharge }) {
  return (
    <Paper sx={{ p: 2 }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Loads</Typography>
        {!loading && !!entries.length && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {entries.length} {entries.length === 1 ? 'trip' : 'trips'}
          </Typography>
        )}
      </Box>

      {loading && <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} /></Box>}

      {!loading && !entries.length && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
          This car has never been on a load.
        </Typography>
      )}

      {!loading && !!entries.length && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Load</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Driver</TableCell>
              {canSeeCharge && <TableCell align="right">Charge</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(({ loadId, load, stop }) => {
              const inbound = stop.type === 'car_pickup';
              const meta = LOAD_STATUS_META[load.status] || LOAD_STATUS_META.pending;
              const cancelled = load.status === 'cancelled';
              // Addresses are stored whole, so they get their own full-width row
              // rather than being clipped into a narrow column. The lot leg is
              // only rendered when the stop actually carries one.
              const lotLeg = inbound ? stop.dropoff : stop.pickup;
              const hasDetail = !!(stop.address || stop.customerName || lotLeg);
              const noBorder = { borderBottom: 'none' };
              return (
                <React.Fragment key={`${loadId}-${stop.type}-${stop.stopType || ''}`}>
                  <TableRow hover>
                    <TableCell sx={{ ...(hasDetail ? noBorder : {}), textDecoration: cancelled ? 'line-through' : 'none' }}>
                      <Link
                        component="button"
                        underline="hover"
                        onClick={() => StateManager.openLoadModal(loadId)}
                        data-sold-ok
                        sx={{ fontWeight: 600 }}
                      >
                        #{loadId}
                      </Link>
                    </TableCell>
                    <TableCell sx={hasDetail ? noBorder : undefined}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {inbound
                          ? <CallReceivedIcon fontSize="small" sx={{ color: 'info.dark' }} />
                          : <LocalShippingIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                        <Typography variant="body2">{inbound ? 'Inbound pickup' : 'Delivery'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={hasDetail ? noBorder : undefined}>
                      <Chip size="small" label={meta.label} color={meta.color} />
                    </TableCell>
                    <TableCell sx={hasDetail ? noBorder : undefined}>{fmtDate(loadDate(load))}</TableCell>
                    <TableCell sx={hasDetail ? noBorder : undefined}>{load.driver || '—'}</TableCell>
                    {canSeeCharge && (
                      <TableCell align="right" sx={hasDetail ? noBorder : undefined}>
                        {stop.charge ? money(stop.charge) : '—'}
                      </TableCell>
                    )}
                  </TableRow>

                  {hasDetail && (
                    <TableRow>
                      <TableCell colSpan={canSeeCharge ? 6 : 5} sx={{ pt: 0, pl: 4 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {inbound ? 'Pick up at' : 'Deliver to'}
                          {stop.customerName ? ` · ${stop.customerName}` : ''}
                        </Typography>
                        {stop.address && (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{stop.address}</Typography>
                        )}
                        {lotLeg && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                            {inbound ? 'Drop at' : 'Departs'} · {lotLeg}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

// ── One trailer type's result ───────────────────────────────────────────────
function TrailerResult({ title, result }) {
  const [showComps, setShowComps] = React.useState(false);

  if (!result) return null;

  if (!result.ok) {
    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: 'warning.dark', mt: 1 }}>{result.error}</Typography>
      </Box>
    );
  }

  const lowConfidence = typeof result.confidence === 'number' && result.confidence < LOW_CONFIDENCE;
  const comps = result.recent_moves || [];
  const price = marked(result.price);
  // Derived from the shown price, not carried over from the raw one — a
  // per-mile rate that doesn't divide out would give the margin away.
  const perMile = result.distance_miles ? (price / result.distance_miles) : result.price_per_mile;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>{title}</Typography>
        {result.cached && (
          <Tooltip title={`Cached ${moment(result.fetched_at).fromNow()} — press Refresh for a live number`}>
            <Chip size="small" variant="outlined" label="cached" />
          </Tooltip>
        )}
      </Box>

      <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5 }}>{money(price)}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {money(marked(result.price_range_lower))} &ndash; {money(marked(result.price_range_upper))}
        {perMile ? ` · $${Number(perMile).toFixed(2)}/mi` : ''}
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <Grid container spacing={1}>
        <Stat label="Confidence" value={result.confidence === null ? '—' : `${result.confidence}%`} />
        <Stat label="Moves / 90d" value={result.volume ?? '—'} />
        <Stat label="Distance" value={result.distance_miles ? `${Math.round(result.distance_miles).toLocaleString()} mi` : '—'} />
        <Stat
          label="To dispatch"
          value={result.max_expected_time_to_dispatch === null
            ? '—'
            : `${Math.round(result.min_expected_time_to_dispatch || 0)}–${Math.round(result.max_expected_time_to_dispatch)} hrs`}
        />
      </Grid>

      {lowConfidence && (
        <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', mt: 1.5 }}>
          Thin data on this lane. Treat the number as a starting point, not a quote.
        </Typography>
      )}

      {/* A saved estimate keeps only the headline numbers — comps are too large
          to store 10 of on the car doc. Refresh pulls them back, usually off the
          server's own cache. */}
      {!comps.length && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Refresh to see recent comparable moves.
        </Typography>
      )}

      {!!comps.length && (
        <>
          <Button
            size="small"
            sx={{ mt: 1 }}
            endIcon={showComps ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowComps(v => !v)}
            data-sold-ok
          >
            {comps.length} recent move{comps.length === 1 ? '' : 's'}
          </Button>
          <Collapse in={showComps}>
            {/* Super Dispatch does not filter these by the trailer type asked
                for, so each row carries its own marker. Without it an open comp
                reads as evidence for an enclosed price. */}
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Super Dispatch returns comps for both trailer types — check the tag on each row.
            </Typography>
            <Table size="small">
              <TableBody>
                {comps.map((move, i) => {
                  const v = (move.vehicles || [])[0] || {};
                  return (
                    <TableRow key={i}>
                      <TableCell sx={{ px: 0.5 }}>
                        <Typography variant="caption">{move.delivered_date || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 0.5 }}>
                        <Typography variant="caption">
                          {[v.year, v.make, v.model].filter(Boolean).join(' ') || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 0.5 }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={v.requires_enclosed_trailer ? 'enclosed' : 'open'}
                          color={v.requires_enclosed_trailer ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.5 }}>
                        <Typography variant="caption">
                          {move.distance_miles ? `${Math.round(move.distance_miles).toLocaleString()} mi` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ px: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{money(move.price)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </>
      )}
    </Box>
  );
}

function Stat({ label, value }) {
  return (
    <Grid item xs={6}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
    </Grid>
  );
}
